import { formatNumber, formatPercent } from "./format.ts";
import { ChartFrame, PatternDefs, PatternedRect, useChartId } from "./frame.tsx";
import { Legend } from "./Legend.tsx";
import { type ChartSeries, resolveSeries } from "./series.ts";
import { useChartStrings } from "./strings.ts";

export interface ShareSegment extends ChartSeries {
  value: number;
}

export interface ShareBarProps {
  title: string;
  summary: string;
  /** In display order, left to right; shares are computed from their sum. */
  segments: readonly ShareSegment[];
  formatValue?: (value: number) => string;
  /** Table header of the label column, e.g. "Citation status". */
  labelHeader: string;
  /** Table header of the count column, e.g. "Citations". */
  valueHeader: string;
  className?: string;
}

/**
 * A 100 % bar: each segment's share of the whole, separated by gaps, patterned, and named in
 * the legend with its share and count.
 */
export function ShareBar({
  title,
  summary,
  segments,
  formatValue = formatNumber,
  labelHeader,
  valueHeader,
  className,
}: ShareBarProps) {
  const id = useChartId();
  const strings = useChartStrings();
  const resolved = resolveSeries(segments);
  const total = segments.reduce((sum, s) => sum + s.value, 0);
  const shareOf = (value: number) => (total > 0 ? value / total : 0);
  const legend = Object.fromEntries(
    segments.map((s) => [s.key, `${formatPercent(shareOf(s.value))} (${formatValue(s.value)})`]),
  );
  const table = {
    columns: [labelHeader, valueHeader, strings.share],
    rows: segments.map((s) => [s.label, formatValue(s.value), formatPercent(shareOf(s.value))]),
  };
  let x = 0;
  return (
    <ChartFrame title={title} summary={summary} table={table} className={className}>
      {({ summaryId }) => (
        <div className="flex flex-col gap-2">
          <svg
            role="img"
            aria-describedby={summaryId}
            viewBox="0 0 100 10"
            preserveAspectRatio="none"
            className="h-4 w-full"
          >
            <title>{title}</title>
            <PatternDefs id={id} />
            {total === 0 ? (
              <rect x={0} y={0} width={100} height={10} className="fill-chart-track" />
            ) : null}
            {resolved.map((s, i) => {
              const width = shareOf(segments[i]?.value ?? 0) * 100;
              const start = x;
              x += width;
              // A hairline gap between segments, so adjacent colours never touch.
              const gap = i > 0 && width > 0 ? 0.3 : 0;
              return width > 0 ? (
                <PatternedRect
                  key={s.key}
                  chartId={id}
                  tone={s.tone}
                  pattern={s.pattern}
                  x={start + gap}
                  y={0}
                  width={Math.max(width - gap, 0)}
                  height={10}
                />
              ) : null;
            })}
          </svg>
          <Legend series={resolved} values={legend} />
        </div>
      )}
    </ChartFrame>
  );
}
